import { ForwardedRef, forwardRef } from 'preact/compat';
import { useEffect, useImperativeHandle, useRef, useState } from 'preact/hooks';

import MediaCell from '@/components/MediaCell';
import { Video, VideoV2 } from '@/queries/media/types';

import { PrimaryButton } from '../Buttons';
import { useIsMobileLayout } from '../LayoutDetector/hooks';
import Loader from '../Loader';
import { useVideoList } from './hooks';

interface HighlightsVideoListProps {
  type: 'curated' | 'clip';
  className?: string;
  play?: boolean;
  formulaHighlightOverride?: number;
  onVideoChange?: (highlight: VideoV2 | Video, index: number) => void;
  onVideoClick?: (highlight: VideoV2 | Video, index: number) => void;
  onLoadMore?: () => void;
  onUpgradeClick?: () => void;
  pageSize?: number;
}

export interface HighlightsVideoListRef {
  currentIndex: () => number | undefined;
  requestPlayVideoAtIndex: (index: number) => void;
  next: () => void;
  prev: (() => void) | undefined;
  stop: () => void;
}

const HighlightsVideoList = (
  {
    type,
    className,
    onVideoChange,
    onVideoClick,
    onLoadMore,
    onUpgradeClick,
    play,
    formulaHighlightOverride,
    pageSize,
  }: HighlightsVideoListProps,
  ref: ForwardedRef<HighlightsVideoListRef>,
) => {
  const isMobile = useIsMobileLayout();
  const listRef = useRef<HTMLUListElement>(null);
  const [videoIndex, setVideoIndex] = useState<number | undefined>();

  const { data, isFetchingNextPage, hasNextPage, fetchNextPage, fetchMoreIfNeeded } = useVideoList(
    type,
    formulaHighlightOverride,
    pageSize,
  );
  const highlights = data?.list;

  const requestPlayVideoAtIndex = (index: number) => {
    if (!data) return;
    const video = highlights?.[index];
    fetchMoreIfNeeded(index);
    if (!video) {
      return;
    }
    setVideoIndex(index);
    onVideoChange?.(video, index);
    if (isMobile) {
      listRef.current?.querySelector(`[data-id="${video._id}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  };

  useImperativeHandle(ref, () => ({
    currentIndex: () => videoIndex,
    requestPlayVideoAtIndex,
    stop: () => {
      setVideoIndex(undefined);
    },
    next: () => {
      if (!data || videoIndex == null) {
        return;
      }
      requestPlayVideoAtIndex((videoIndex + 1) % data.list.length);
    },
    prev:
      data && videoIndex != null && videoIndex - 1 > 0
        ? () => {
            requestPlayVideoAtIndex((videoIndex - 1 + data.list.length) % data.list.length);
          }
        : undefined,
  }));

  const videoChangeHandler = (video: VideoV2, index: number) => {
    if (!data) return;
    fetchMoreIfNeeded(index);
    setVideoIndex(index);
    onVideoChange?.(video, index);
  };

  const loadMoreHandler = () => {
    fetchNextPage();
    onLoadMore?.();
  };

  // Play the first video when the list is requested to play when data is loaded
  const isPlayable = (highlights?.length ?? 0) > (videoIndex ?? 0);
  const isPlaying = videoIndex != null;
  const requestPlayVideoAtIndexRef = useRef(requestPlayVideoAtIndex);
  requestPlayVideoAtIndexRef.current = requestPlayVideoAtIndex; // We change the ref here to avoid the useEffect from being called on every render
  useEffect(() => {
    if (play && isPlayable && !isPlaying) {
      requestPlayVideoAtIndexRef.current?.(0);
    }
  }, [play, isPlayable, isPlaying]);

  return (
    <div className={className}>
      <ul ref={listRef} className="preflight preflight-ul grid grid-cols-1 gap-6 desktop:grid-cols-3 desktop:gap-3">
        {highlights?.map((highlight, index) => (
          <li
            data-group="Highlight List Item"
            key={highlight._id}
            data-id={highlight._id}
            data-title={highlight.title}
            className="mobile:scroll-m-6"
          >
            <MediaCell
              data-native
              media={highlight}
              mediaId={highlight._id}
              href={`/highlights?type=${type}&id=${highlight._id}`}
              isPlaying={videoIndex === index}
              aria-disabled={highlight.disabled}
              onClick={(e) => {
                e.preventDefault();
                videoChangeHandler(highlight, index);
                onVideoClick?.(highlight, index);
              }}
            />
          </li>
        ))}
      </ul>
      {isFetchingNextPage && <Loader color="var(--charcoal)" className="my-4" width="64px" height="64px" />}
      {hasNextPage &&
        !isFetchingNextPage &&
        (onUpgradeClick ? (
          <div className="my-4 flex flex-col items-center gap-[11px]">
            <PrimaryButton
              label="Upgrade for Unlimited Clips"
              size={isMobile ? 'medium' : 'large'}
              className="mobile:!text-[15px] mobile:!font-medium desktop:text-sm desktop:!font-normal"
              onClick={onUpgradeClick}
            />
            <p className="font-bold text-primary-green mobile:whitespace-nowrap mobile:text-xs mobile:leading-4 desktop:text-xs">
              Or come back tomorrow for new daily highlights!
            </p>
          </div>
        ) : (
          <div className="my-4 flex justify-center">
            <PrimaryButton label="Load More" size={isMobile ? 'medium' : 'large'} onClick={loadMoreHandler} />
          </div>
        ))}
    </div>
  );
};

export default forwardRef(HighlightsVideoList);
